import {
  ASSET_STORE_EXPERIMENT_USER_GROUP,
  ASSET_STORE_USER_GROUP,
} from '@lib/coveo/coveoConstants';
import {
  SearchTokenResponse,
  getSearchTokenGroups,
} from '@lib/coveo/searchTokenHelpers';

/**
 * Coveo context for the header engine.
 *
 * Query suggestions come from an ML model attached to a query pipeline our org
 * selects with a Condition on `userGroups` (see coveoConstants). A querySuggest
 * call carrying no context matches no Condition and comes back with zero
 * completions — the same token and query that returns five suggestions with
 * `userGroups` returns none without it. Both results engines (CSR
 * createEngineModule, SSR SearchSsrPage) set these three values, so sending
 * them here also keeps the header's suggestions and the results page they lead
 * to on one pipeline.
 */
export function headerSearchContextValues(
  locale: string,
  token: SearchTokenResponse,
  inExperimentGroup: boolean
): Record<string, string | string[]> {
  return {
    website: 'assetstore',
    language: locale,
    userGroups: headerUserGroups(token, inExperimentGroup),
  };
}

function headerUserGroups(
  token: SearchTokenResponse,
  inExperimentGroup: boolean
): string[] {
  const groups = tokenUserGroups(token);
  // The header holds an ANONYMOUS token, which the token route deliberately
  // leaves experiment-pipeline routing out of; the SSR results page adds the
  // group from the same flag server-side (SearchSsrPage), and without it the
  // header would suggest from the control pipeline to shoppers whose results
  // come from the experiment one.
  return inExperimentGroup &&
    !groups.includes(ASSET_STORE_EXPERIMENT_USER_GROUP)
    ? [...groups, ASSET_STORE_EXPERIMENT_USER_GROUP]
    : groups;
}

function tokenUserGroups(token: SearchTokenResponse): string[] {
  if (token.userGroups.length > 0) {
    return token.userGroups;
  }
  // A pod still serving the previous release answers with a bare token string,
  // which fetchSearchToken reports as an empty group list. The JWT carries the
  // groups either way, and an empty list is exactly what silences suggestions.
  try {
    const decoded = getSearchTokenGroups(token.token);
    return decoded?.length ? decoded : [ASSET_STORE_USER_GROUP];
  } catch {
    return [ASSET_STORE_USER_GROUP];
  }
}
